import { reactive } from 'vue';
import { itemIs, useNotif } from '@orion.ui/orion';
import { USER_STATUS } from '@/typings/enums/USER_STATUS';
import { ORDER_USER_STATUS } from '@/typings/enums/ORDER_USER_STATUS';
import { qsTo64 } from '@/utils/tools';
import router from '@/router/index.router';
import useRolePermissionsService from '@/services/RolePermissionService';
import BaseEntity from '@/entity/BaseEntity';
import useUserService from '@/services/user/UserService';
import useBusService from '@/services/BusService';

type CandidateRating = {
	numberOfRating: number,
	rating: number,
}

type OrderUserWithoutUser = Omit<IOrderUserAgencyListItemModel, 'user'>;
export default class TempWorkerListItemEntity<T extends Armado.TempWorkerListItemEntityType = Armado.TempWorkerListItemEntityType> extends BaseEntity<T> {

	private readonly privateState = reactive({
		sendingEmail: false,
		rating: {} as CandidateRating,
		orderUser: undefined as Undef<OrderUserWithoutUser>,
	});

	get authLogin () { return this.state.authLogin; }
	get availabilityDate () { return this.state.availabilityDate; }
	get avatarId () { return this.state.avatarId; }
	get hasToConfirmEmail () { return +this.state.status === USER_STATUS.HasToConfirmedEmail; }
	get isActivated () { return +this.state.status === USER_STATUS.Activated; }
	get isAvailable () { return this.state.isAvailable; }
	get isInactivated () { return +this.state.status === USER_STATUS.Inactivated; }
	get isNotDemat () { return +this.state.status === USER_STATUS.Created; }
	get name () { return this.state.firstName + ` ` + this.state.lastName; }
	get numberOfContracts () { return this.state.numberOfContracts; }
	get numberOfRating () { return this.state.numberOfRating; }
	get mobilePhone () { return this.state.personalInformationMobilePhone; }
	get professionalSkills () { return this.state.professionalSkills; }
	get rating () { return this.state.rating; }
	get sendingEmail () { return this.privateState.sendingEmail; }
	get status () { return this.state.status; }
	get talents () { return this.state.talents; }
	get userProfileCommentsCount () { return this.state.userProfileCommentsCount; }
	get userTasksCount () { return this.state.userTasksCount; }

	get orderUserStepTitle () { return this.privateState.orderUser?.stepUserOrderStepTitle; }
	get orderUserStatus () { return this.privateState.orderUser?.status; }
	get orderUserId () { return this.privateState.orderUser?.id; }

	get profileIsPublic () {
		return itemIs<IUserCandidateModel>(this.state, 'profileIsPublic')
			? this.state.profileIsPublic
			: true;
	}

	get numberOfContractsWithClient () {
		if (itemIs<IUserCandidateModel>(this.state, 'numberOfContractsWithClient'))
			return this.state.numberOfContractsWithClient;
		return undefined;
	}

	get isAvailableNow () {
		if (this.availabilityDate) {
			const date = new Date(this.availabilityDate).toMidnight();
			const now = new Date().toMidnight();

			return date <= now;
		}
		return false;
	}

	get availability () {
		if (this.isAvailable === null) {
			return {
				color: 'default',
				tooltip: 'Non renseignée',
			};
		} else if (this.isAvailable || this.isAvailableNow) {
			return {
				color: 'success',
				tooltip: 'Disponible',
			};
		} else {
			return {
				color: 'warning',
				tooltip: 'Non disponible',
			};
		}
	}

	get localisationString () {
		const stringToReturn = [];

		if (this.state.personalInformationCity) {
			stringToReturn.push(this.state.personalInformationCity);
		}
		if (this.state.personalInformationPostCode) {
			stringToReturn.push(this.state.personalInformationPostCode);
		}
		if (this.state.personalInformationCountry) {
			stringToReturn.push(this.state.personalInformationCountry);
		}

		return stringToReturn.join(', ');
	}

	get candidateStatus () {
		switch (this.orderUserStatus) {
		case ORDER_USER_STATUS.InSteps:
			return {
				label: this.orderUserStepTitle,
				color: 'default' as Orion.Color,
			};
		case ORDER_USER_STATUS.Validated:
			return {
				label: `Validé`,
				color: 'success' as Orion.Color,
			};
		case ORDER_USER_STATUS.Rejected:
			return {
				label: `Candidat disqualifié`,
				color: 'danger' as Orion.Color,
			};
		default:
			return undefined;
		}
	}


	constructor (payload?: T, orderUser?: IOrderUserAgencyListItemModel) {
		super(payload);
		this.privateState.orderUser = orderUser;

		useBusService().on(`${this.id}:add-talent-pool`, (talentPool: ITalentPoolForProfileModel) => {
			this.addTalentPool(talentPool);
		});

		useBusService().on(`${this.id}:remove-talent-pool`, (talentPool: ITalentPoolForProfileModel) => {
			this.removeTalentPool(talentPool);
		});

		useBusService().on(`${this.id}-list-item:ranking-update`, (rating: number) => {
			this.state.rating = rating;
		});
	}


	addTalentPool (talentPool: ITalentPoolForProfileModel) {
		this.state.talents.push({
			...talentPool,
			talentId: 0,
		});
	}

	removeTalentPool (talentPool: ITalentPoolForProfileModel) {
		this.state.talents.deleteWhere('id', talentPool.id);
	}

	async sendActivationMailAsync () {
		await useRolePermissionsService().permissionGuardAsync('TempWorkerUserRead');

		this.privateState.sendingEmail = true;

		try {
			await useUserService().sendEmailActivationAsync(this.id);
			useNotif.success(`L'email a bien été envoyé`);
		} catch (e: any) {
			useNotif.danger(`Oops`, e?.response?.data);
		} finally {
			this.privateState.sendingEmail = false;
		}
	}

	async sendEmailConfirmationAsync () {
		await useRolePermissionsService().permissionGuardAsync('TempWorkerUserRead');

		this.privateState.sendingEmail = true;

		try {
			await useUserService().sendEmailConfirmationTempWorkerAsync(this.id);
			useNotif.success(`L'email a bien été envoyé`);
		} catch (e: any) {
			useNotif.danger(`Oops`, e?.response?.data);
		} finally {
			this.privateState.sendingEmail = false;
		}
	}

	goToContracts () {
		if (!this.self.can('TempWorkerContractRead')) return;
		if (!this.numberOfContracts) return;

		if (this.self.isAgency()) {
			const routeData = router.resolve({
				name: 'TempWorkerContract',
				query: {
					filter: qsTo64({
						research: this.state.authLogin,
						showCanceled: true,
					}),
				},
			});
			window.open(routeData.href, '_blank');
		}
	}

	openTempWorkerAsideDetails (highlightElement?: string) {
		const routeName = router.currentRoute.value.name;

		if (routeName === 'TempWorker') {
			router.push({
				name: 'TempWorkerDetails',
				params: { id: this.id },
				query: highlightElement ? { highlightElement } : undefined,
			});
		}
	}
}
